<template>
  <div :id="chartId"></div>
</template>

<script>
import "@/assets/css/signal.css"
import zingchart from 'zingchart';

import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick, getCurrentInstance } from 'vue';
import { mapActions, mapState } from 'vuex';
import { useStore } from 'vuex';

export default {
  name: 'RatioChart',
  setup() {
    const { proxy } = getCurrentInstance();
    const chartId = ref('zingchart-' + Math.random().toString(36).substr(2, 9));
    const store = useStore();
    const ratioInfo = computed(() => store.state.signal.ratioData);
    const ratioRange = computed(() => store.state.signal.range);
    const language = computed(() => store.state.dominance.lang);
    const windowWidth = ref(window.innerWidth);

    const getLegendLayout = ref(windowWidth.value > 600 ? 'x3' : 'x1');

    const getTranslatedText = () => ({
      long: proxy.$t('signal.long'),
      short: proxy.$t('signal.short'),
      btcPrice: proxy.$t('pub.btcPrice'),
    });

    const translatedText = getTranslatedText();

    const chartData = ref({
      type: "mixed",
      globals: {
        fontFamily: "var(--main-font-Light)",
        fontColor: 'var(--color-main)',
        fontSize: '15px',
      },
      gui: { contextMenu: { visible: false } },
      backgroundColor: "transparent",
      legend: {
        layout: getLegendLayout.value,
        backgroundColor: 'transparent',
        borderColor: 'none',
        overflow: 'page',
        alpha: 0.05,
        shadow: false,
        align: 'center',
        marker: { 'border-color': 'none', size: '8px' },
        item: { fontColor: 'var(--color-main)' },
        'toggle-action': 'none',
      },
      'scale-x': { values: [], guide: { visible: false } },
      'scale-y': { values: "0:100:25", guide: { visible: false } },
      'scale-y-2': {
        guide: { visible: false },
        "thousands-separator": ",",
      },
      'crosshair-x': {
        offsetY: '10%',
        marker: { visible: false },
        plotLabel: {
          fontColor: "var(--color-main)",
          fontFamily: "var(--main-font-Light)",
          text: "",
        },
      },
      plot: {
        tooltip: { visible: false },
        hoverState: { visible: false },
        highlightState: { visible: false },
      },
      series: [
        {
          type: "bar",
          text: translatedText.long,
          scales: "scale-x,scale-y",
          borderRadiusBottomLeft: '30px',
          borderRadiusBottomRight: '30px',
          values: [],
          'background-color': "var(--signal-green1) var(--signal-green2)",
          stacked: true,
          stack: 0.1,
          "bar-width": "100%",
          "guide-label": {
            "backgroundColor": "var(--background-chart2)",
            "text": "%t: %v %",
          }
        },
        {
          type: "bar",
          text: translatedText.short,
          scales: "scale-x,scale-y",
          borderRadiusTopLeft: '30px',
          borderRadiusTopRight: '30px',
          values: [],
          'background-color': "var(--signal-red2) var(--chart-red)",
          stacked: true,
          stack: 0.1,
          "bar-width": "100%",
          "guide-label": { "text": "%t: %v %" },
        },
        {
          type: "line",
          text: translatedText.btcPrice,
          legendMarker: {
            'border-width': 1,
            'border-color': "var(--color-main)",
            type: "circle",
            'show-line': true,
            'line-color': "var(--color-main)",
            'size': '7px',
          },
          "line-color": "var(--color-main)",
          "line-width": 3,
          scales: "scale-x,scale-y-2",
          values: [],
          marker: {
            "background-color": "var(--color-main)",
            "border-width": 2,
            shadow: 0,
            "border-color": "var(--color-main)",
          },
          guideLabel: {
            text: '%t: $%v',
            "thousands-separator": ",",
          },
        },
      ],
    });

    const renderChart = () => {
      zingchart.render({
        id: chartId.value,
        data: chartData.value,
        height: '450px',
        width: '100%', // 폭을 고정하거나 퍼센트 유지
      });
      nextTick(() => {
        zingchart.exec(chartId.value, 'resize');
      });
    };

    const fontLang = () => {
      if (language.value === 'ja') return "Noto Sans JP";
      if (language.value === 'zh') return "Noto Sans SC";
      return "var(--main-font-Light)";
    };

    
    const updateChartData = (newData) => {
      getLegendLayout.value = windowWidth.value > 768 ? 'x3' : 'x1';
      chartData.value.legend.layout = getLegendLayout.value;
      chartData.value.globals.fontFamily = fontLang();
      const limitedCount = windowWidth.value <= 1024 ? 8 : newData.long.length;
      chartData.value.series[0].values = newData.long.slice(0, limitedCount);
      chartData.value.series[1].values = newData.short.slice(0, limitedCount);
      chartData.value.series[2].values = newData.price.slice(0, limitedCount);
      chartData.value['scale-x'].values = newData.datatime.slice(0, limitedCount);

      zingchart.exec(chartId.value, 'setdata', {
        data: chartData.value,
      });

      zingchart.exec(chartId.value, 'resize');
    };
    
    const removeBtn = async() => {
      nextTick(() => {
        const toolBar = document.querySelector(`#${chartId.value}-trigger-c`);
        const zingBtn = document.querySelector(`#${chartId.value}-license-text`);

        if (zingBtn) zingBtn.style.display = 'none';
        if (toolBar) toolBar.style.display = 'none';
      });
    };

    const loadAndUpdateChart = async () => {
      await store.dispatch('signal/loadRatioData');
      if (ratioInfo.value) {
        updateChartData(ratioInfo.value);
      }
      await removeBtn();
    };

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
      if (ratioInfo.value) updateChartData(ratioInfo.value);
    };

    onMounted(async () => {
      renderChart();
      await loadAndUpdateChart();
      window.addEventListener('resize', handleResize);
    });

    onBeforeUnmount(() => {
      zingchart.exec(chartId.value, 'destroy');
      window.removeEventListener('resize', handleResize);
    });

    watch(() => store.state.signal.time, async () => {
      await loadAndUpdateChart();
    });

    watch(ratioRange, (newRange) => {
      if (newRange) {
        chartData.value['scale-y-2'].values = newRange;
        renderChart();
      }
    });

    watch(language, () => {
      chartData.value.globals.fontFamily = fontLang();
      renderChart();
    });

    watch(windowWidth, async() => {
      await nextTick();
      await loadAndUpdateChart();
      removeBtn();
    });

    return { chartId };
  },
  computed: {
    ...mapState("signal", ["ratioData", "time", "range"]),
  },
  methods: {
    ...mapActions("signal", ["loadRatioData"]),
  },
};
</script>

<style>
#chartId {
  width: 100%;
  height: 500px;
}
@media (max-width: 1200px) {
  #chartId {
    height: 300px;
  }
}
</style>
